import { useMemo, useState, type CSSProperties } from "react";
import { formatDateInZone } from "@nurion/datetime/format-date-in-zone";

import { LOCAL_ZONE } from "~/lib/relative-time";
import { useUnderline } from "~/lib/underline";

const MENU_WIDTH = 280;

export interface SendLaterMenuProps {
  anchor: HTMLElement | null;
  onSchedule: (sendAt: number) => void;
  onClose: () => void;
  now?: number;
}

type Preset = {
  label: string;
  resolve: (now: number) => number;
};

function nextTomorrowNine(now: number): number {
  const date = new Date(now);
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return date.getTime();
}

function nextMondayNine(now: number): number {
  const date = new Date(now);
  const day = date.getDay();
  const daysUntilMonday = (8 - day) % 7 || 7;
  date.setDate(date.getDate() + daysUntilMonday);
  date.setHours(9, 0, 0, 0);
  return date.getTime();
}

const PRESETS: Preset[] = [
  { label: "In 15 minutes", resolve: (now) => now + 15 * 60_000 },
  { label: "In 1 hour", resolve: (now) => now + 60 * 60_000 },
  { label: "Tomorrow 9am", resolve: nextTomorrowNine },
  { label: "Monday 9am", resolve: nextMondayNine },
];

export function SendLaterMenu({
  anchor,
  onSchedule,
  onClose,
  now = Date.now(),
}: SendLaterMenuProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState(() => toDateTimeLocal(now + 60 * 60_000));
  const [customError, setCustomError] = useState<string | null>(null);
  const underline = useUnderline();

  const rect = anchor?.getBoundingClientRect() ?? null;
  const style = useMemo<CSSProperties>(() => {
    if (!rect) return { display: "none" };
    return {
      position: "fixed",
      zIndex: 200,
      width: MENU_WIDTH,
      left: Math.max(8, rect.right - MENU_WIDTH),
      top: Math.max(8, rect.top - 8),
      transform: "translateY(-100%)",
      borderRadius: "var(--chat-radius-xl)",
      border: "1px solid var(--chat-surface-border)",
      background: "var(--chat-surface-overlay)",
      color: "var(--chat-fg-primary)",
      boxShadow: "var(--chat-shadow-popover)",
      padding: "var(--chat-space-2)",
      fontFamily: "var(--chat-font-sans)",
    };
  }, [rect]);

  const submitCustom = () => {
    const date = new Date(customValue);
    const sendAt = date.getTime();
    if (!Number.isFinite(sendAt) || sendAt <= Date.now()) {
      setCustomError("Choose a future time.");
      return;
    }
    setCustomError(null);
    onSchedule(sendAt);
  };

  return (
    <div role="dialog" aria-label="Send later" style={style}>
      <div
        style={{
          padding: "var(--chat-space-2) var(--chat-space-2) var(--chat-space-1)",
          color: "var(--chat-fg-secondary)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        Send later
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onSchedule(preset.resolve(now))}
            className="chat-button"
            style={menuButtonStyle}
          >
            <span>{preset.label}</span>
            <span style={secondaryTextStyle}>{formatAbsolute(preset.resolve(now))}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCustomOpen((open) => !open)}
          className="chat-button"
          style={menuButtonStyle}
        >
          <span>Custom</span>
        </button>
      </div>
      {customOpen && (
        <div
          style={{ padding: "var(--chat-space-2)", display: "flex", gap: "var(--chat-space-2)" }}
        >
          <input
            type="datetime-local"
            value={customValue}
            onChange={(e) => {
              setCustomValue(e.currentTarget.value);
              setCustomError(null);
            }}
            onFocus={underline.onFocus}
            onBlur={underline.onBlur}
            style={{
              minWidth: 0,
              flex: 1,
              border: "none",
              borderBottom: `1px solid ${underline.borderColor}`,
              background: "transparent",
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              outline: "none",
              padding: "0 0 var(--chat-space-1)",
              transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
            }}
          />
          <button
            type="button"
            onClick={submitCustom}
            className="chat-button"
            style={ghostButtonStyle}
          >
            Schedule
          </button>
          {customError && <span style={errorTextStyle}>{customError}</span>}
        </div>
      )}
      <button
        type="button"
        onClick={onClose}
        className="chat-button"
        style={{ ...ghostButtonStyle, marginTop: "var(--chat-space-1)" }}
      >
        Close
      </button>
    </div>
  );
}

const menuButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "var(--chat-space-2)",
  width: "100%",
  minHeight: 32,
  border: "none",
  borderRadius: "var(--chat-radius-sm)",
  background: "transparent",
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  textAlign: "left",
  cursor: "pointer",
  padding: "var(--chat-space-1) var(--chat-space-2)",
};

const secondaryTextStyle: CSSProperties = {
  color: "var(--chat-fg-secondary)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
  flexShrink: 0,
};

const ghostButtonStyle: CSSProperties = {
  border: "none",
  borderRadius: "var(--chat-radius-sm)",
  background: "var(--chat-surface-panel)",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  cursor: "pointer",
  padding: "var(--chat-space-1) var(--chat-space-2)",
};

const errorTextStyle: CSSProperties = {
  color: "var(--chat-state-danger)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
};

function toDateTimeLocal(ts: number): string {
  const date = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function formatAbsolute(ts: number): string {
  return formatDateInZone(new Date(ts).toISOString(), LOCAL_ZONE, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}
